import type { CentrifugeMessage, ParsedCentrifugeMessage } from '@module/types/centrifuge/messageTypes';
import {
  isLiveRoomRankMessageUnparsed,
  type LiveRoomRankMessage,
  type LiveRoomRankMessageUnparsed,
} from '@module/types/centrifuge/public-message/etcMessageTypes';
import {
  type EventAnimCoinMessage,
  type EventAnimCoinMessageUnparsed,
  isEventAnimCoinMessageUnparsed,
  isLayoutChatMessageUnparsed,
  type LayoutChatMessage,
  type LayoutChatMessageUnparsed,
} from '@module/types/centrifuge/public-message/eventMessageTypes';
import {
  isMissionMessageUnparsed,
  type MissionMessage,
  type MissionMessageUnparsed,
} from '@module/types/centrifuge/public-message/missionMessageTypes';
import {
  isAdminChatMessage,
  isNormalChatMessage,
} from '@module/types/centrifuge/public-message/normalChatMessageTypes';
import parseJsonSafely from '../../utils/parseJsonSafely';

export type LayoutEventMessageUnparsed =
  | LayoutChatMessageUnparsed
  | EventAnimCoinMessageUnparsed
  | LiveRoomRankMessageUnparsed;

export type LayoutEventMessage = LayoutChatMessage | EventAnimCoinMessage | LiveRoomRankMessage;

/**
 * 레이아웃/이벤트 메시지 파싱 (message와 opt를 이중 파싱)
 */
export function parseLayoutEventMessage(payload: LayoutEventMessageUnparsed): LayoutEventMessage {
  const parsedMessage = JSON.parse(payload.data.message);
  const parsedOpt = JSON.parse(parsedMessage.opt);

  return {
    data: {
      ...payload.data,
      message: { ...parsedMessage, opt: parsedOpt },
    },
    offset: payload.offset,
  } as LayoutEventMessage;
}

/**
 * 미션 메시지 파싱 (message와 내부 data를 파싱)
 */
export function parseMissionMessage(payload: MissionMessageUnparsed): MissionMessage {
  const parsedMessage = JSON.parse(payload.data.message);

  if (parsedMessage.data) {
    parsedMessage.data = JSON.parse(parsedMessage.data);
  }

  return {
    data: {
      ...payload.data,
      message: parsedMessage,
    },
    offset: payload.offset,
  } as MissionMessage;
}

/**
 * 일반 문자열 메시지 파싱
 */
export function parseStringMessage(payload: CentrifugeMessage): ParsedCentrifugeMessage {
  const parsedMessage = parseJsonSafely(payload.data.message as string);

  if (typeof parsedMessage === 'string') {
    return payload as ParsedCentrifugeMessage;
  }

  return {
    ...payload,
    data: {
      ...payload.data,
      message: parsedMessage,
    },
  } as ParsedCentrifugeMessage;
}

/**
 * 메인 파서 함수 - 메시지 타입에 따라 적절한 파서를 선택
 */
function parseSocketPayload(payload: CentrifugeMessage): ParsedCentrifugeMessage {
  // 1. 사용자의 입력에 의한 채팅 메세지(NormalChatMessage, AdminChatMessage)는 파싱 불필요
  if (isNormalChatMessage(payload) || isAdminChatMessage(payload)) {
    return payload as ParsedCentrifugeMessage;
  }

  // 2. 레이아웃/이벤트/랭크 메시지
  if (
    isLiveRoomRankMessageUnparsed(payload) ||
    isLayoutChatMessageUnparsed(payload) ||
    isEventAnimCoinMessageUnparsed(payload)
  ) {
    return parseLayoutEventMessage(payload);
  }

  // 3. 미션 메시지
  if (isMissionMessageUnparsed(payload)) {
    return parseMissionMessage(payload);
  }

  // 4. 그 외 message가 string인 경우 파싱
  if ('data' in payload && typeof payload.data.message === 'string') {
    return parseStringMessage(payload);
  }

  // 5. default
  return payload as ParsedCentrifugeMessage;
}

export default parseSocketPayload;
